import { type FC, useEffect, useRef } from 'react'

import { ProductCarouselGalleryImageItem } from './ProductCarouselGalleryImageItem'
import { type ProductCarouselGalleryProps } from './ProductCarouselGalleryProps'
import { ProductCarouselGalleryVideoItem } from './ProductCarouselGalleryVideoItem'

const ProductCarouselGallery: FC<ProductCarouselGalleryProps> = ({
  currentImageIndex,
  images,
  onImageScrolledInView,
  onItemClick,
  onTabNavigation,
  playVideoOnClick,
  playVideoOnScrolledIntoView,
  productName,
  useLargeImageSizes,
  videos,
  zoomEnabled = false,
}) => {
  const numberOfMediaObjects = images.length + videos.length
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      const pixelsPerImage =
        containerRef.current.scrollWidth / numberOfMediaObjects

      const nextScrollLeftPosition = currentImageIndex * pixelsPerImage

      containerRef.current.scrollTo({
        behavior: 'smooth',
        left: nextScrollLeftPosition,
      })
    }
  }, [currentImageIndex, numberOfMediaObjects])

  const handleScrolledIntoView = (index: number): void => {
    onImageScrolledInView(index)
  }

  return (
    <div
      className="absolute top-0 flex size-full snap-x snap-mandatory flex-nowrap overflow-auto rounded-lg border border-transparent scrollbar-hide hover:border-dark-primary-min"
      ref={containerRef}
    >
      {images.map((image, index) => {
        return (
          <ProductCarouselGalleryImageItem
            alt={image.alt_text ?? productName}
            image={image.public_url}
            index={index}
            key={image.public_url}
            lazy={index > 0}
            onClick={(): void => {
              if (onItemClick) {
                onItemClick(index)
              }
            }}
            onScrolledInView={(): void => {
              handleScrolledIntoView(index)
            }}
            onTabNavigation={onTabNavigation}
            useLargeSizes={useLargeImageSizes}
            zoomEnabled={zoomEnabled}
          />
        )
      })}
      {videos.map((video, index) => {
        const globalIndex = images.length + index

        return (
          <ProductCarouselGalleryVideoItem
            index={globalIndex}
            key={video.code}
            lazy={globalIndex > 0}
            onClick={(): void => {
              if (onItemClick) {
                onItemClick(globalIndex)
              }
            }}
            onScrolledInView={(): void => {
              handleScrolledIntoView(globalIndex)
            }}
            onTabNavigation={onTabNavigation}
            playOnClick={playVideoOnClick}
            playOnScrolledIntoView={playVideoOnScrolledIntoView}
            productName={productName}
            useLargeSizes={useLargeImageSizes}
            video={video}
            zoomEnabled={zoomEnabled}
          />
        )
      })}
    </div>
  )
}

export { ProductCarouselGallery }
